import { useEffect, useState, useMemo } from 'react';
import { database } from '../config/firebase';
import { ref, onValue, set, onDisconnect, serverTimestamp } from 'firebase/database';
import { getToken } from '../utils/auth';

export const useStreamViewers = (streamId: string | undefined) => {
    const [viewerCount, setViewerCount] = useState(0);

    // Generate a unique session ID for guests that persists per session
    const sessionId = useMemo(() => {
        let id = sessionStorage.getItem('viewer_session_id');
        if (!id) {
            id = Date.now().toString(36) + Math.random().toString(36).substring(2);
            sessionStorage.setItem('viewer_session_id', id);
        }
        return id;
    }, []);

    useEffect(() => {
        if (!streamId) return;

        // Determine user identity
        // If we have a token, we might decode it, but for simple counting, 
        // the session ID is enough to track "devices/tabs". 
        // If you want to track "Users" specifically, you'd use userId.
        // For "Views", session/tab concept is usually better or hybrid.
        // Let's use sessionId to be safe for guests too.

        const viewerRef = ref(database, `live_viewers/${streamId}/${sessionId}`);
        const streamViewersRef = ref(database, `live_viewers/${streamId}`);
        const connectedRef = ref(database, '.info/connected');

        // Handle connection state
        const unsubscribeConnected = onValue(connectedRef, (snap) => {
            if (snap.val() === true) {
                // Remove on disconnect
                onDisconnect(viewerRef).remove();

                // Set user as present
                set(viewerRef, {
                    last_seen: serverTimestamp(),
                    device: navigator.userAgent
                });
            }
        });

        // Listen for count updates
        const unsubscribeCount = onValue(streamViewersRef, (snap) => {
            if (snap.exists()) {
                setViewerCount(Object.keys(snap.val()).length);
            } else {
                setViewerCount(0);
            }
        });

        return () => {
            unsubscribeConnected();
            unsubscribeCount();
            // Optional: Remove self on unmount (leaving the page)
            set(viewerRef, null);
        };
    }, [streamId, sessionId]);

    return viewerCount;
};
